import React, { useState } from 'react';
import { HotelAvailabilityErrors, parseBoolean, TripType, useCurrentTripId, useGlobalContext, useHotelBasketSummary, useRooms, useRoomsAndFlight, useSearch, } from '@honeycomb/data';
import { boardSelectionRouting, hotelContinueToBookRouting, reduceMockErrorAttemptsRouting } from '@honeycomb/routing';
import { Button, useBreakpoints, useDisclosure } from '@honeycomb/ui-core';
import { BetaEstabAction, FunnelAction, trackBetaEstabAction, trackBetaEstabActionRetry, trackBetaEstabActionRetrySuccess, trackFunnelAction, } from '@honeycomb/tracking';
import { getNextCheapestRoom, isHotelOnlyTransferEnabledforChannel } from '../../../../utils';
import { HotelBasketActionType, useHotelBasketContext } from '../../HotelBasketProvider';
import { HotelInterstitial } from '../../HotelInterstitial/HotelInterstitial';
import { isCachedNavlError } from '../../utils/isCachedNavlError';
import { ErrorNavlRetryModal } from '../HotelBasket/ErrorNavlRetryModal';
import { ErrorNavlRoomUpdateModal } from '../HotelBasket/ErrorNavlRoomUpdateModal';
export function HotelConfirmSelectionButton(props) {
    var _a, _b, _c, _d, _e;
    const { mediumUp } = useBreakpoints();
    const { resourceStrings: { CONTINUETOBOOK }, configSettings: { HCEnableHotelOnlyTransfers, HCHotelonlyTransferChannels, EnableMockErrors }, } = useGlobalContext();
    const [interstitialModalOpen, { open: handleOpenInterstitialModal, close: handleCloseInterstitialModal }] = useDisclosure(false);
    const [navlRetryModalOpen, { open: handleOpenNavlRetryModal, close: handleCloseNavlRetryModal }] = useDisclosure(false);
    const [navlRoomChangeModalOpen, { open: handleNavlRoomChangeOpen, close: handleCloseNavlRoomChangeModal }] = useDisclosure(false);
    const [roomConfirmed, setRoomConfirmed] = useState(false);
    // Using a state to keep track of cachedNavl erorr happening as the errors in
    // context gets cleared once basketSummary is re-called.
    const [cachedNavl, setCachedNavl] = useState(false);
    const tripId = (_b = (_a = useCurrentTripId()[0].data) === null || _a === void 0 ? void 0 : _a.currentTripStateId) !== null && _b !== void 0 ? _b : '';
    const [{ tripType, destinations }] = useSearch([]);
    const isHoliday = tripType === TripType.Holiday;
    const { dispatch, hotelBasket: { estabId, selectedRooms, selectedFlight, tripStateId, waitingForFlights, waitingForRooms, availabilityErrors, unavailableRoomIds, }, } = useHotelBasketContext();
    const flightDetailsId = isHoliday ? (_c = selectedFlight === null || selectedFlight === void 0 ? void 0 : selectedFlight.flightDetailsId) !== null && _c !== void 0 ? _c : null : null;
    const hotelSummaryResult = useHotelBasketSummary(estabId, [], [], !(tripType === TripType.Holiday || tripType === TripType.Hotel));
    const isHotelOnlyTransfersEnabled = parseBoolean(HCEnableHotelOnlyTransfers) && isHotelOnlyTransferEnabledforChannel(HCHotelonlyTransferChannels);
    const confirmSideEffect = React.useCallback((tripState) => {
        setRoomConfirmed(true);
        if (cachedNavl) {
            trackBetaEstabActionRetrySuccess(BetaEstabAction.CTB_ERROR_CACHED_NAVL, tripType !== null && tripType !== void 0 ? tripType : 'Unknown');
            setCachedNavl(false);
        }
        const redirectFlag = isHoliday ? tripStateId === tripId : false;
        const bookingPageUrl = hotelContinueToBookRouting({
            summaryRouting: !mediumUp,
            tripState,
            optionalHotelTransfersEnabled: isHotelOnlyTransfersEnabled,
        }, redirectFlag);
        if (redirectFlag) {
            window.location.href = bookingPageUrl;
        }
    }, [cachedNavl, isHoliday, tripStateId, tripId, mediumUp, isHotelOnlyTransfersEnabled, tripType]);
    const errorSideEffect = React.useCallback((error) => {
        var _a, _b, _c;
        // 409 error is a Navl error, which means the room is not available.
        const isNavlError = error.message.includes('409');
        const hasCachedNavlError = isCachedNavlError(error, destinations);
        handleCloseInterstitialModal();
        setRoomConfirmed(false);
        if (!isNavlError) {
            dispatch({
                type: HotelBasketActionType.SET_AVAILABILITY_ERRORS,
                payload: hasCachedNavlError
                    ? HotelAvailabilityErrors.CACHED_NAVL_ERROR
                    : HotelAvailabilityErrors.CONTINUE_TO_BOOK_ERROR,
            });
        }
        trackFunnelAction(FunnelAction.SELECT_ROOM_ERROR, tripType !== null && tripType !== void 0 ? tripType : 'Unknown', {
            error: error.message,
            estabId,
        });
        trackBetaEstabAction(hasCachedNavlError ? BetaEstabAction.CTB_ERROR_CACHED_NAVL : BetaEstabAction.CTB_ERROR, tripType !== null && tripType !== void 0 ? tripType : 'Unknown', {
            error: error.message,
        });
        // Handle room not available error for single room selection only for now.
        if (isNavlError && selectedRooms.length === 1) {
            const roomNotAvailableBoardId = selectedRooms[0].boardRoomId;
            const nextCheapestRoom = getNextCheapestRoom((_c = (_b = (_a = hotelSummaryResult === null || hotelSummaryResult === void 0 ? void 0 : hotelSummaryResult.data) === null || _a === void 0 ? void 0 : _a.hotelRoomsAvailability) === null || _b === void 0 ? void 0 : _b.boardOptions) !== null && _c !== void 0 ? _c : [], [...unavailableRoomIds, roomNotAvailableBoardId]);
            dispatch({
                type: HotelBasketActionType.SET_ROOM_NOT_AVAILABLE,
                payload: roomNotAvailableBoardId,
            });
            if (nextCheapestRoom) {
                dispatch({
                    type: HotelBasketActionType.SET_SELECTED_ROOM,
                    payload: { selectedRooms: [nextCheapestRoom], automaticRoomSelection: true },
                });
                boardSelectionRouting(nextCheapestRoom.boardRoomId);
            }
            else {
                dispatch({
                    type: HotelBasketActionType.SET_AVAILABILITY_ERRORS,
                    payload: HotelAvailabilityErrors.CONTINUE_TO_BOOK_ERROR,
                });
            }
            handleNavlRoomChangeOpen();
        }
        else if (isNavlError && selectedRooms.length > 1) {
            dispatch({
                type: HotelBasketActionType.SET_AVAILABILITY_ERRORS,
                payload: HotelAvailabilityErrors.CONTINUE_TO_BOOK_ERROR,
            });
        }
        if (hasCachedNavlError) {
            handleOpenNavlRetryModal();
            setCachedNavl(true);
        }
        if (parseBoolean(EnableMockErrors)) {
            reduceMockErrorAttemptsRouting();
        }
    }, [
        destinations,
        handleCloseInterstitialModal,
        tripType,
        estabId,
        selectedRooms,
        EnableMockErrors,
        dispatch,
        (_e = (_d = hotelSummaryResult === null || hotelSummaryResult === void 0 ? void 0 : hotelSummaryResult.data) === null || _d === void 0 ? void 0 : _d.hotelRoomsAvailability) === null || _e === void 0 ? void 0 : _e.boardOptions,
        unavailableRoomIds,
        handleNavlRoomChangeOpen,
        handleOpenNavlRetryModal,
    ]);
    const { selectRooms, selectRoomsState } = useRooms([confirmSideEffect], [errorSideEffect]);
    const { selectRoomsAndFlight, selectRoomsAndFlightState } = useRoomsAndFlight([confirmSideEffect], [errorSideEffect]);
    const isWaitingForSelection = isHoliday ? waitingForFlights || waitingForRooms : waitingForRooms;
    const hasSelection = isHoliday ? (selectedRooms === null || selectedRooms === void 0 ? void 0 : selectedRooms.length) > 0 && (selectedFlight === null || selectedFlight === void 0 ? void 0 : selectedFlight.flightId) : (selectedRooms === null || selectedRooms === void 0 ? void 0 : selectedRooms.length) > 0;
    const handleConfirm = React.useCallback(() => {
        if (selectedRooms.length === 0) {
            // No rooms selected
            return;
        }
        if (cachedNavl) {
            trackBetaEstabActionRetry(BetaEstabAction.CTB_ERROR_CACHED_NAVL, tripType !== null && tripType !== void 0 ? tripType : 'Unknown');
        }
        if (availabilityErrors.includes(HotelAvailabilityErrors.CONTINUE_TO_BOOK_ERROR))
            dispatch({
                type: HotelBasketActionType.REMOVE_AVAILABILITY_ERRORS,
                payload: HotelAvailabilityErrors.CONTINUE_TO_BOOK_ERROR,
            });
        handleOpenInterstitialModal();
        trackFunnelAction(FunnelAction.SELECT_ROOM, tripType !== null && tripType !== void 0 ? tripType : 'Unknown');
        trackBetaEstabAction(BetaEstabAction.CTB, tripType !== null && tripType !== void 0 ? tripType : 'Unknown');
        if (isHoliday) {
            if (!(selectedFlight === null || selectedFlight === void 0 ? void 0 : selectedFlight.flightId)) {
                // No flights selected
                handleCloseInterstitialModal();
                return;
            }
            selectRoomsAndFlight({
                estabId: Number(estabId),
                roomIds: selectedRooms.map((r) => r.boardRoomId),
                tripStateId,
                flightDetailsId: flightDetailsId !== null && flightDetailsId !== void 0 ? flightDetailsId : '',
            });
        }
        else {
            selectRooms({
                estabId: Number(estabId),
                roomIds: selectedRooms.map((r) => r.boardRoomId),
                tripStateId,
            });
        }
    }, [
        selectedRooms,
        cachedNavl,
        availabilityErrors,
        dispatch,
        handleOpenInterstitialModal,
        tripType,
        isHoliday,
        selectedFlight === null || selectedFlight === void 0 ? void 0 : selectedFlight.flightId,
        selectRoomsAndFlight,
        estabId,
        tripStateId,
        flightDetailsId,
        handleCloseInterstitialModal,
        selectRooms,
    ]);
    const handleCloseAndClearError = () => {
        handleCloseNavlRetryModal();
        dispatch({
            type: HotelBasketActionType.REMOVE_AVAILABILITY_ERRORS,
            payload: HotelAvailabilityErrors.CACHED_NAVL_ERROR,
        });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, Object.assign({ color: "callToAction", size: "l", fullWidth: true, onClick: handleConfirm, disabled: isWaitingForSelection || !hasSelection, loading: selectRoomsState.loading || selectRoomsAndFlightState.loading, "data-id": "confirm-selection-button" }, props), CONTINUETOBOOK),
        React.createElement(HotelInterstitial, { open: interstitialModalOpen, onClose: handleCloseInterstitialModal, progressBarComplete: roomConfirmed }),
        React.createElement(ErrorNavlRetryModal, { open: navlRetryModalOpen, onClose: handleCloseAndClearError }),
        React.createElement(ErrorNavlRoomUpdateModal, { open: navlRoomChangeModalOpen, handleClick: handleCloseNavlRoomChangeModal })));
}
